// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@awc/XDL/types";
import { autoClamp } from "@awc/XDL/Utilities";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const fontConfig = {
    url: "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100..900;1,100..900&display=swap",
    weight: {
        regular: 400,
        medium: 500,
        bold: 700,
    },
    fontSize: {
        xxSmall: 10,
        xSmall: 12,
        small: 14,
        regular: 15,
        headLine: 40,
        headLineMobile: 28,
        link: 19,
        singleLineRegular: 16,
        pageTitle: 34,
        sectionTitle: 24,
    },
    lineHeight: {
        xxSmall: "12px",
        xSmall: "18px",
        small: "22px",
        regular: "23px",
        headLine: "47px",
        headLineMobile: "38px",
        singleLinexxSmall: "14px",
        singleLinexSmall: "14px",
        singleLineSmall: "16px",
        singleLineRegular: "18px",
    },
    fontFamily: "'Roboto', 'Lato', Ariel, sans-serif",
};

export default {
    fontFamilyImportUrl: fontConfig.url,
    body: {
        size: "15px",
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        size: "15px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 15px;
            letter-spacing: 0;
            line-height: 23px;
            display: block;
        `,
    },
    h1: {
        size: autoClamp(34, 36, 40),
        weight: fontConfig.weight.bold,
        color: "primary.main",
        lineHeight: "47px",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h2: {
        size: autoClamp(22, 22, 24),
        weight: fontConfig.weight.bold,
        color: "primary.main",
        lineHeight: "28px",
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h3: {
        size: autoClamp(18, 18, 19),
        weight: fontConfig.weight.bold,
        color: "primary.main",
        lineHeight: "23px",
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h4: {
        size: "16px",
        weight: fontConfig.weight.bold,
        lineHeight: "19px",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h5: {
        size: "16px",
        weight: fontConfig.weight.bold,
        lineHeight: "19px",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    h6: {
        size: "16px",
        weight: fontConfig.weight.bold,
        lineHeight: "19px",
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.fontFamily,
        color: "text",
    },
    headerTertiary: {
        forwardAs: "p",
        size: "18px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.fontFamily,
        color: "text",
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        color: "text",
        transform: "uppercase",
        weight: fontConfig.weight.regular,
    },
} as xBaseTheme["typography"];
